import { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Loader2, Edit } from 'lucide-react';

import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useCreators } from '@/hooks/use-creators';
import CreatorUploadModal from './CreatorUploadModal';
import { Creator } from '@/types/creators';
import { PermissionGuard } from '@/components/PermissionGuard';
import { useAuth } from '@/contexts/auth';
import { hasPermission } from '@/utils/permissions';

export default function CreatorsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const queryParams = useMemo(() => ({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined
  }), [currentPage, debouncedSearchTerm]);

  const { creators, total, loading, refetch } = useCreators(queryParams);
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'edit_content');

  const currentItems = creators || [];

  const formatFollowers = (count: number) => {
    if (count >= 1000000) {
      return `${(count / 1000000).toFixed(1)}M`;
    } else if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  };

  const getAudienceSizeColor = (size: string) => {
    switch (size) {
      case 'NANO': return 'bg-gray-100 text-gray-800';
      case 'MICRO': return 'bg-blue-100 text-blue-800';
      case 'MACRO': return 'bg-purple-100 text-purple-800';
      case 'MEGA': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getInfluenceTypeColor = (type: string) => {
    switch (type) {
      case 'ALPHA': return 'bg-green-100 text-green-800';
      case 'BETA': return 'bg-yellow-100 text-yellow-800';
      case 'MAINSTREAM': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-end mb-6">
        <div className="relative w-full sm:w-auto">
          <Input
            placeholder="Procurar creators..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-full sm:w-64"
          />
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 stroke-gray-400 w-4 h-4" />
        </div>
        <PermissionGuard action="create_content">
          <Button 
            onClick={() => setIsUploadModalOpen(true)}
            className="flex items-center gap-2 bg-black text-white hover:bg-gray-900 w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" />
            Novo
          </Button>
        </PermissionGuard>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="ml-2">Carregando creators...</span>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Handle Principal</TableHead>
              <TableHead>Tamanho da Audiência</TableHead>
              <TableHead>Tipo de Influência</TableHead>
              <TableHead>Status</TableHead>
              {canEdit && <TableHead className="text-right">Ações</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={canEdit ? 6 : 5} className="text-center py-8 text-gray-500">
                  Nenhum creator encontrado
                </TableCell>
              </TableRow>
            ) : (
              currentItems.map((creator: Creator) => (
                <TableRow key={creator.id} className="h-[60px]">
                  <TableCell className="font-medium">{creator.name}</TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{creator.handle}</div>
                      <div className="text-sm text-gray-500">{creator.main_platform}</div>
                      <div className="text-xs text-gray-400">
                        {formatFollowers(creator.main_platform_followers)} seguidores
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Badge 
                        variant="secondary" 
                        className={getAudienceSizeColor(creator.audience_size)}
                      >
                        {creator.audience_size}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant="secondary"
                      className={getInfluenceTypeColor(creator.influence_type)}
                    >
                      {creator.influence_type}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge 
                      variant={creator.is_active ? 'default' : 'secondary'}
                      className={creator.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    >
                      {creator.is_active ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      <button className="p-1 rounded-md border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors">
                        <Edit className="w-4 h-4" />
                      </button>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      )}

      {!loading && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}

      <CreatorUploadModal
        isOpen={isUploadModalOpen}
        onOpenChange={setIsUploadModalOpen}
        onSuccess={() => {
          refetch();
        }}
      />
    </div>
  );
}
